import {
  ScenarioHandler,
  BaseScenario,
  ScenarioResult,
  AssistantContext,
} from "../types";
import {
  TicketInternalStatus,
  TicketLabel,
  TicketPriority,
} from "shared/types/helpdesk";
import { parseAnalyticsQuery } from "../../../utils/analyticsQueryParser";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

/**
 * Ticket Scenario Handler
 *
 * Handelt twee soorten acties:
 * 1. Ticket aanmaken: "Maak een ticket voor @2000-EMIE-12..."
 * 2. Ticket analytics: "Hoeveel vochtschade tickets heb ik?"
 */
export class TicketScenario implements ScenarioHandler {
  examples = [
    "Maak een ticket voor @2000-EMIE-12 @UNIT-001, waar het raam defect is",
    "Maak een ticket voor @2000-EMIE-12, unit 001, waar het raam defect is",
    "Hoeveel tickets ivm vochtschade heb ik?",
    "Geef mij alle tickets ikv huurdersdiscussies die momenteel open staan",
    "Toon alle open tickets voor @2000-EMIE-12",
    "Waar heb ik de meeste elektriciteitsproblemen?",
    "Hoe evolueren tickets voor @2000-EMIE-12 over tijd?",
    "Hoeveel liftstoringen waren er deze maand?",
    "Waarover gaat ticket H323?",
    "Wat is de status van ticket H456?",
  ];

  detect(input: string): BaseScenario {
    const normalized = input.toLowerCase();

    // Ticket analysis pattern: "waarover gaat ticket H123" or "analyseer ticket H456"
    const ticketAnalysisPattern =
      /(waarover gaat|analyseer|wat is de status van|status|huidige stand) ticket h?\d+/i;
    if (ticketAnalysisPattern.test(normalized)) {
      return { type: "ticket-analysis", confidence: 0.95 };
    }

    // Ticket creation keywords
    const createKeywords = [
      "maak een ticket",
      "nieuw ticket",
      "ticket voor",
      "ticket maken",
    ];
    const hasCreateKeyword = createKeywords.some((kw) =>
      normalized.includes(kw),
    );

    // Analytics keywords - expanded for natural language
    const analyticsKeywords = [
      "hoeveel",
      "waar",
      "welk",
      "welke",
      "evolutie",
      "evolueren",
      "evolueer",
      "trend",
      "over tijd",
      "statistiek",
      "overzicht",
      "meeste",
      "minste",
      "probleem",
      "problemen",
      "aantal",
      "telling",
      "geef",
      "toon",
      "laat zien",
      "laat me zien",
      "lijst",
      "alle",
      "overzicht",
    ];
    const hasAnalyticsKeyword = analyticsKeywords.some((kw) =>
      normalized.includes(kw),
    );

    // List query keywords (high confidence for filtered-list queries)
    const listKeywords = [
      "geef mij alle",
      "geef alle",
      "toon alle",
      "laat alle zien",
      "lijst van",
    ];
    const hasListKeyword = listKeywords.some((kw) => normalized.includes(kw));

    // Label keywords (suggest tickets are involved) - expanded with plurals and variations
    const labelKeywords = [
      "vocht",
      "water",
      "vochtschade",
      "lek",
      "lekkage",
      "elektriciteit",
      "elektrisch",
      "stroom",
      "verwarming",
      "cv",
      "radiator",
      "raam",
      "ramen",
      "venster",
      "deur",
      "deuren",
      "voordeur",
      "sanitair",
      "toilet",
      "wc",
      "lift",
      "elevator",
      "internet",
      "wifi",
      "verlichting",
      "lamp",
      "ventilatie",
      "afzuiging",
      "dak",
      "goot",
      "tuin",
      "groen",
      "brand",
      "brandmelder",
    ];
    const hasLabelKeyword = labelKeywords.some((kw) => normalized.includes(kw));

    // Ticket creation has priority
    if (hasCreateKeyword) {
      return { type: "ticket-create", confidence: 0.9 };
    }

    // List queries with high confidence (e.g., "Geef mij alle tickets...")
    if (hasListKeyword && normalized.includes("ticket")) {
      return { type: "ticket-analytics", confidence: 0.95 };
    }

    // Analytics with high confidence if it mentions tickets explicitly
    if (hasAnalyticsKeyword && normalized.includes("ticket")) {
      return { type: "ticket-analytics", confidence: 0.9 };
    }

    // Analytics with good confidence if it has both analytics intent and a label
    if (hasAnalyticsKeyword && hasLabelKeyword) {
      return { type: "ticket-analytics", confidence: 0.85 };
    }

    // Medium confidence if just has analytics keywords
    if (hasAnalyticsKeyword) {
      return { type: "ticket-analytics", confidence: 0.6 };
    }

    // Lower confidence if just has label keywords (might be asking about that category)
    if (hasLabelKeyword) {
      return { type: "ticket-analytics", confidence: 0.5 };
    }

    return { type: "unknown", confidence: 0 };
  }

  async parse(input: string): Promise<ScenarioResult> {
    const detection = this.detect(input);

    if (detection.type === "ticket-create") {
      return this.parseTicketCreation(input);
    } else if (detection.type === "ticket-analytics") {
      return this.parseTicketAnalytics(input);
    } else if (detection.type === "ticket-analysis") {
      return this.parseTicketAnalysis(input);
    }

    throw new Error("Unknown ticket scenario");
  }

  private parseTicketCreation(input: string): ScenarioResult {
    // Detect building ID: @2000-EMIE-12 or 2000-EMIE-12
    const buildingMatch = input.match(/@?(\d{4}-[A-Z]+-\d{2})\b/i);
    const buildingId = buildingMatch ? buildingMatch[1].toUpperCase() : null;

    // Detect unit ID: @UNIT-xxx or unit 001 or box 001
    let unitId: string | null = null;

    // First try @ mention format: @UNIT-xxx
    const unitMentionMatch = input.match(/@(UNIT-[\w-]+)/i);
    if (unitMentionMatch) {
      unitId = unitMentionMatch[1].toUpperCase();
    } else {
      // Fall back to natural language: "unit 001" or "box 001"
      const unitNaturalMatch =
        input.match(/unit\s+([\w-]+)/i) || input.match(/box\s+([\w-]+)/i);
      if (unitNaturalMatch && buildingId) {
        // For natural language, construct the full unit ID
        unitId = `${buildingId}-${unitNaturalMatch[1].toUpperCase()}`;
      }
    }

    const label = this.classifyLabel(input);
    const priority = this.determinePriority(input);

    if (!buildingId) {
      throw new Error("Geen gebouw ID gevonden");
    }

    return {
      scenario: "ticket-create",
      action: "create",
      data: {
        buildingId,
        unitId,
        description: input,
        label,
        priority,
      },
      preview: {
        buildingId,
        unitId,
        label,
        priority,
        description: input,
      },
    };
  }

  private parseTicketAnalytics(input: string): ScenarioResult {
    const query = parseAnalyticsQuery(input);

    // Debug logging
    console.log("🔍 Analytics query parsed:", {
      input,
      detectedType: query.type,
      detectedLabel: query.filters.label,
      buildingId: query.filters.buildingId,
      chartType: query.chartType,
    });

    return {
      scenario: "ticket-analytics",
      action: "analyze",
      data: query,
      chartConfig: {
        type: query.chartType || "bar",
        title: query.title,
        data: [], // Will be filled by execute()
      },
    };
  }

  private parseTicketAnalysis(input: string): ScenarioResult {
    // Extract ticket ID from input: "waarover gaat ticket H123" or "ticket 123"
    const ticketIdMatch = input.match(/ticket\s+h?(\d+)/i);

    if (!ticketIdMatch) {
      throw new Error("Geen geldig ticket ID gevonden");
    }

    const ticketId = parseInt(ticketIdMatch[1], 10);

    return {
      scenario: "ticket-analysis",
      action: "query",
      data: {
        ticketId,
      },
    };
  }

  async execute(
    result: ScenarioResult,
    context: AssistantContext,
  ): Promise<any> {
    if (result.action === "create") {
      return this.executeTicketCreation(result, context);
    } else if (result.action === "analyze") {
      return this.executeTicketAnalytics(result, context);
    } else if (
      result.action === "query" &&
      result.scenario === "ticket-analysis"
    ) {
      return this.executeTicketAnalysis(result, context);
    }

    throw new Error("Unknown action");
  }

  private async executeTicketCreation(
    result: ScenarioResult,
    context: AssistantContext,
  ): Promise<any> {
    const { buildingId, unitId, description, label, priority } = result.data;

    const payload = {
      title: description.substring(0, 100),
      description,
      label,
      priority,
      internalStatus: TicketInternalStatus.NEW_UNANSWERED,
      source: "Intern",
      building: { id: buildingId },
      unit: unitId ? { id: unitId } : null,
    };

    const response = await fetchWithAuth(
      `${context.apiUrl}/helpdesk/ticket`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      context.session,
    );

    if (!response.ok) {
      throw new Error("Failed to create ticket");
    }

    return await response.json();
  }

  private async executeTicketAnalytics(
    result: ScenarioResult,
    context: AssistantContext,
  ): Promise<any> {
    const query = result.data;

    console.log("🤖 Calling AI endpoint with query:", query);

    // Converteer Date objecten naar ISO strings voor API
    const apiQuery = {
      type: query.type,
      filters: {
        buildingId: query.filters.buildingId,
        unitId: query.filters.unitId,
        label: query.filters.label,
        startDate: query.filters.startDate?.toISOString(),
        endDate: query.filters.endDate?.toISOString(),
        includeClosedTickets: query.filters.includeClosedTickets,
      },
      granularity: query.granularity,
      title: query.title,
    };

    // Call de nieuwe AI endpoint
    // Dit endpoint handelt alle routing en data formatting af
    const response = await fetchWithAuth(
      `${context.apiUrl}/helpdesk/ai/analytics`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(apiQuery),
      },
      context.session,
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Failed to fetch analytics from AI endpoint",
      );
    }

    const responseData = await response.json();

    console.log("🤖 AI endpoint response:", {
      type: responseData.type,
      hasData: !!responseData.data,
      dataLength: responseData.data?.length,
      hasTickets: !!responseData.tickets,
      ticketsLength: responseData.tickets?.length,
      totalCount: responseData.totalCount,
    });

    // De AI endpoint retourneert al het correcte formaat
    // Geen extra extractie of transformatie nodig
    return responseData;
  }

  private async executeTicketAnalysis(
    result: ScenarioResult,
    context: AssistantContext,
  ): Promise<any> {
    const { ticketId } = result.data;

    console.log("🤖 Analyzing ticket H" + ticketId);

    // Call backend endpoint to analyze ticket
    const response = await fetchWithAuth(
      `${context.apiUrl}/helpdesk/ai/analyze-ticket/${ticketId}`,
      {
        method: "GET",
      },
      context.session,
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to analyze ticket");
    }

    const analysisData = await response.json();

    console.log("🤖 Ticket analysis response:", analysisData);

    // Return formatted response for display
    return {
      type: "ticket-analysis",
      title: `Analyse van Ticket H${ticketId}`,
      chartType: "table",
      data: analysisData,
    };
  }

  private classifyLabel(text: string): TicketLabel {
    const normalized = text.toLowerCase();

    const labelKeywords: Record<TicketLabel, string[]> = {
      [TicketLabel.Buitenschrijnwerk]: [
        "raam",
        "ramen",
        "venster",
        "glas",
        "voordeur",
        "buitendeur",
      ],
      [TicketLabel.Glasbreuk]: ["glasbreuk", "gebroken glas"],
      [TicketLabel.Binnenschrijnwerk]: ["deur", "deuren", "binnendeur"],
      [TicketLabel.VochtWater]: [
        "vocht",
        "water",
        "lek",
        "insijpel",
        "nat",
        "schimmel",
        "vochtschade",
        "lekkage",
      ],
      [TicketLabel.Verwarming]: [
        "verwarming",
        "radiator",
        "koud",
        "cv",
        "warm",
      ],
      [TicketLabel.Elektriciteit]: [
        "stroom",
        "elektriciteit",
        "elektriciteits",
        "stopcontact",
        "schakelaar",
        "elektriciteitsproblemen",
        "elekticiteitsproblemen",
        "elektrisch",
        "electrisch",
      ],
      [TicketLabel.Sanitair]: [
        "toilet",
        "wc",
        "kraan",
        "douche",
        "verstopt",
        "sanitair",
      ],
      [TicketLabel.Verlichting]: ["lamp", "licht", "verlichting", "tl", "led"],
      [TicketLabel.Ventilatie]: ["ventilatie", "afzuiging", "lucht", "rooster"],
      [TicketLabel.Toegangsbeheer]: [
        "slot",
        "sloten",
        "deurslot",
        "cilinder",
        "toegang",
      ],
      [TicketLabel.Lift]: ["lift", "elevator"],
      [TicketLabel.Dak]: ["dak", "dakgoot", "goot"],
      [TicketLabel.Riolering]: ["riool", "afvoer", "geur", "riolering"],
      [TicketLabel.Onderhoud]: ["onderhoud", "controle"],
      [TicketLabel.Poetsen]: ["opkuis", "poetsen", "schoonmaak", "vuil"],
      [TicketLabel.AfvalEnOngedierte]: [
        "ongedierte",
        "ratten",
        "muizen",
        "kakkerlakken",
        "afval",
      ],
      [TicketLabel.Huurdersdiscussies]: [
        "huurincasso",
        "huurdersdiscussies",
        "huurders",
        "klacht",
      ],
      [TicketLabel.HVAC]: ["hvac", "airco", "klimaat"],
      [TicketLabel.Afbraakwerken]: [
        "afbraak",
        "afbreken",
        "slopen",
        "demolitie",
        "verwijderen",
      ],
      [TicketLabel.Asbest]: ["asbest", "asbesthoudend", "asbestverwijdering"],
      [TicketLabel.Balustrade]: ["balustrade", "leuning", "reling", "hekwerk"],
      [TicketLabel.Brandbeveiliging]: [
        "brand",
        "brandmelder",
        "brandveiligheid",
        "sprinkler",
        "brandblusser",
        "rookmelder",
      ],
      [TicketLabel.ElastischeVoegen]: [
        "voeg",
        "voegen",
        "kit",
        "kitten",
        "afdichting",
      ],
      [TicketLabel.ElectronischeToestellen]: [
        "toestel",
        "apparaat",
        "elektronica",
        "wasmachine",
        "droogkast",
        "oven",
        "vaatwasser",
      ],
      [TicketLabel.Nutsvoorzieningen]: [
        "nuts",
        "nutsvoorziening",
        "aansluiting",
        "energieaansluiting",
      ],
      [TicketLabel.Boekhouding]: [
        "boekhouding",
        "administratie",
        "financieel",
        "boekhoudkundig",
      ],
      [TicketLabel.Facturatie]: [
        "factuur",
        "facturatie",
        "rekening",
        "betaling",
      ],
      [TicketLabel.Gebouwattributen]: [
        "gebouwkenmerk",
        "attribuut",
        "eigenschap",
        "specificatie",
      ],
      [TicketLabel.Gevel]: ["gevel", "buitenmuur", "voorgevel", "zijgevel"],
      [TicketLabel.Inrichting]: ["inrichting", "interieur", "decoratie"],
      [TicketLabel.Internet]: [
        "internet",
        "wifi",
        "netwerk",
        "verbinding",
        "connectivity",
      ],
      [TicketLabel.Karweien]: [
        "karwei",
        "karweien",
        "klusje",
        "klusjes",
        "klein onderhoud",
      ],
      [TicketLabel.Overige]: ["overig", "overige", "anders", "diversen"],
      [TicketLabel.Rolluik]: ["rolluik", "zonnescherm", "screen", "screens"],
      [TicketLabel.Ruwbouw]: ["ruwbouw", "structuur", "beton", "draagmuur"],
      [TicketLabel.Stabiliteit]: [
        "stabiliteit",
        "scheur",
        "scheuren",
        "verzakking",
        "constructie",
      ],
      [TicketLabel.Verstopping]: [
        "verstopping",
        "verstopt",
      ],
      [TicketLabel.SuggestiesHuurdersVerbeteringen]: [
        "suggestie",
        "voorstel",
        "verbetering",
        "idee",
      ],
      [TicketLabel.Trap]: ["trap", "trappen", "traphal", "trappenhuis"],
      [TicketLabel.VergunningAttest]: [
        "vergunning",
        "attest",
        "certificaat",
        "conformiteit",
      ],
      [TicketLabel.VloerenParket]: [
        "vloer",
        "vloeren",
        "parket",
        "laminaat",
        "tegels",
        "plavuizen",
      ],
      [TicketLabel.VragenHuurcontract]: [
        "huurcontract",
        "contract",
        "huurovereenkomst",
        "lease",
      ],
      [TicketLabel.Tuinonderhoud]: [
        "tuin",
        "tuinonderhoud",
        "groen",
        "gras",
        "planten",
        "beplanting",
      ],
      [TicketLabel.Signalisatie]: [
        "signalisatie",
        "bord",
        "bewegwijzering",
        "pictogram",
      ],
      [TicketLabel.Meubilair]: [
        "meubilair",
        "meubel",
        "meubelen",
        "kast",
        "tafel",
        "stoel",
      ],
      [TicketLabel.Bezetting]: [
        "bezetting",
        "kraak",
        "illegaal",
        "ongeoorloofd",
      ],
      [TicketLabel.Garage]: [
        "garage",
        "garagebox",
        "garagepoort",
        "garagedeur",
        "poort",
        "parking",
        "parkeerplaats",
        "staanplaats",
      ],
      [TicketLabel.UNKNOWN]: [],
    };

    for (const [label, keywords] of Object.entries(labelKeywords)) {
      if (keywords.some((kw) => normalized.includes(kw))) {
        return label as TicketLabel;
      }
    }

    return TicketLabel.UNKNOWN;
  }

  private determinePriority(text: string): TicketPriority {
    const normalized = text.toLowerCase();
    const highPriorityKeywords = [
      "urgent",
      "spoed",
      "gevaarlijk",
      "geen water",
      "geen stroom",
      "lekkage",
    ];
    const mediumPriorityKeywords = [
      "defect",
      "kapot",
      "werkt niet",
      "probleem",
    ];

    if (highPriorityKeywords.some((kw) => normalized.includes(kw))) {
      return TicketPriority.HIGH;
    }
    if (mediumPriorityKeywords.some((kw) => normalized.includes(kw))) {
      return TicketPriority.MEDIUM;
    }
    return TicketPriority.LOW;
  }
}
